import React from 'react';
import { Page, Text, View, Document, StyleSheet, Link, Font } from '@react-pdf/renderer';

// The template renders the bullet itself. Remove a marker that is already
// present in stored rich text so imported and hand-entered entries match.
const LEADING_BULLET = /^[•‣▪●○◦·⁃∙]+\s*|^[-*–—]\s+/;

// Function to convert HTML to plain text with bullet points
function convertToPlain(html) {
  if (!html) return '';

  // First clean up any empty tags and line breaks
  let cleanHtml = html
    .replace(/<p><br\/?><\/p>/g, '') // Remove empty paragraphs with br
    .replace(/<br\/?>/g, '') // Remove br tags
    .replace(/&amp;/g, '&') // Convert &amp; back to &
    .trim();

  // Check if <ul> tag is already present
  const ulRegex = /<ul[^>]*>/i;
  const olRegex = /<ol[^>]*>/i;

  let wrappedHtml;
  if (!ulRegex.test(cleanHtml) && !olRegex.test(cleanHtml)) {
    const lines = cleanHtml.split('\n').filter(line => line.trim());
    const wrappedLines = lines.map(line => `<li>${line.trim()}</li>`).join('\n');
    wrappedHtml = `<ul>\n${wrappedLines}\n</ul>`;
  } else {
    wrappedHtml = cleanHtml;
  }

  // Remove list tags but preserve structure
  const withoutOlTags = wrappedHtml.replace(/<\/?ol>/g, '');
  const withoutUlTags = withoutOlTags.replace(/<\/?ul>/g, '');

  // Process li tags and handle text wrapping
  let result = withoutUlTags
    .replace(/<li>/g, '') // Remove li tags
    .replace(/<\/li>/g, '\n') // Add newline for each item
    .replace(/<[^>]+>/g, '') // Remove remaining HTML tags
    .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
    .split('\n') // Split into lines
    .map(line => line.trim().replace(LEADING_BULLET, '')) // Trim and drop an existing bullet
    .filter(line => line.length > 0) // Remove empty lines
    .join('\n'); // Join with newlines
   return result
}

function convertToPlain1(html) {
  let tempDivElement = document.createElement('div')

  tempDivElement.innerHTML = html

  return tempDivElement.textContent || tempDivElement.innerText || ''
}
// Create styles
const styles = StyleSheet.create({
  page: {
    padding: '35px 40px',
    fontSize: 9,
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 4,
    textAlign: 'center',
  },
  name: {
    fontSize: 18,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase',
  },
  contactInfo: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    fontFamily: 'Helvetica',
  },
  section: {
    marginBottom: 4,
  },
  sectionHeader: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  sectionHeader1: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  sectionDivider: {
    borderBottom: 1,
    borderBottomColor: '#ddd',
    marginVertical: 2,
  },
  educationItem: {
    marginBottom: 8,
  },
  educationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 2,
    flexWrap: 'wrap',
    gap: 8,
  },
  educationDegree: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    flex: 1,
    marginRight: 8,
  },
  educationDate: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    minWidth: 'auto',
    textAlign: 'right',
  },
  educationInstitution: {
    fontSize: 9,
    fontFamily: 'Helvetica',
  },
  experienceItem: {
    marginBottom: 6,
  },
  experienceHeader: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    width: '100%',
    marginBottom: 4,
    flexWrap: 'wrap',
  },
  experienceTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    flex: 1,
    marginRight: 16,
    flexWrap: 'wrap',
    textTransform: 'capitalize',
  },
  experienceDate: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'right',
    width: '100px',
    marginLeft: 'auto',
  },
  experienceDescription: {
    fontSize: 9,
    marginLeft: 20,
    marginTop: 2,
    lineHeight: 1.5,
    fontFamily: 'Helvetica',
  },
  experienceDescription1: {
    fontSize: 9,
    lineHeight: 1.5,
    marginTop: 3,
    fontFamily: 'Helvetica',
  },
  bulletLine: {
    marginBottom: 2,
    marginLeft: 8,
    breakInside: 'avoid',
    breakBefore: 'auto',
    breakAfter: 'auto',
    display: 'flex',
    flexDirection: 'row',
  },
  bulletText: {
    fontSize: 9,
    lineHeight: 1.4,
    fontFamily: 'Helvetica',
    textAlign: 'justify',
    flex: 1,
  },
  bullet: {
    width: 12,
    fontSize: 9,
    marginRight: 4,
  },
  bullet1: {
    marginHorizontal: 4,
    fontFamily: 'Helvetica',
    fontSize: 15,
    lineHeight: 1.2,
  },
  skills: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    fontSize: 9,
    lineHeight: 1.4,
    marginLeft: 24,
    marginBottom: 4,
  },
  link: {
    fontSize: 9,
    color: '#004C99',
    fontFamily: 'Helvetica',
    borderBottom: 1,
    borderBottomColor: '#004C99',
    borderBottomStyle: 'solid',
  },
  divider: {
    fontSize: 9,
    marginHorizontal: 4,
    fontFamily: 'Helvetica',
  },
  projectItem: {
    marginBottom: 8,
  },
  projectHeader: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    width: '100%',
    marginBottom: 4,
    flexWrap: 'wrap',
  },
  projectTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    flex: 1,
    marginRight: 16,
    flexWrap: 'wrap',
    textTransform: 'capitalize',
  },
  projectDate: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'right',
    width: '100px',
    marginLeft: 'auto',
  },
  projectDescription: {
    fontSize: 9,
    marginTop: 2,
    marginLeft: 20,
    lineHeight: 1.5,
    fontFamily: 'Helvetica',
  },
  certification: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
    marginLeft: 8,
  },
  certificationBullet: {
    marginRight: 12,
    fontSize: 9,
    fontFamily: 'Helvetica',
    lineHeight: 1.2,
  },
  certificationText: {
    fontSize: 9,
    flex: 1,
    fontFamily: 'Helvetica',
  },
  socialLinks: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    marginTop: 2,
    marginBottom: 4,
  },
  link: {
    color: '#004C99',
    textDecoration: 'none',
    fontSize: 9,
    fontFamily: 'Helvetica',
  },

});

const NewResumeTemplate = ({ data }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.name}>{data?.student?.fullName || ''}</Text>
        <View style={styles.contactInfo}>
          <Link src={`mailto:${data?.student?.email}`}>
            <Text style={styles.link}>{data?.student?.email || ''}</Text>
          </Link>
          <Text style={styles.bullet1}>{'\u2022'}</Text>
          <Text style={{ fontSize: 9, fontFamily: 'Helvetica' }}>{'+'+data?.student?.countryCode || ''} {data?.student?.mobile || ''}</Text>
          <Text style={styles.bullet1}>{'\u2022'}</Text>
          <Text style={{ fontSize: 9, fontFamily: 'Helvetica' }}>{data?.student?.city || ''}, {data?.student?.state || ''}</Text>
        </View>
        <View style={styles.socialLinks}>
          {[
            data?.student?.linkedinUrl && (
              <Link src={data.student.linkedinUrl}>
                <Text style={styles.link}>LinkedIn</Text>
              </Link>
            ),
            data?.student?.githubUrl && (
              <Link src={data.student.githubUrl}>
                <Text style={styles.link}>Github</Text>
              </Link>
            ),
            data?.student?.behanceUrl && (
              <Link src={data.student.behanceUrl}>
                <Text style={styles.link}>Behance</Text>
              </Link>
            )
          ].filter(Boolean).map((link, index, arr) => (
            <React.Fragment key={index}>
              {link}
              {index < arr.length - 1 && <Text style={styles.divider}> | </Text>}
            </React.Fragment>
          ))}
        </View>
        <View style={styles.sectionDivider} />
      </View>

      {data?.student?.objective && (
        <View style={styles.section}>
          <Text style={styles.sectionHeader1}>Summary</Text>
          <Text style={styles.experienceDescription1}>{convertToPlain1(data.student.objective)}</Text>
          {data?.education?.length > 0 && (
            <View style={styles.sectionDivider} />
          )}
        </View>
      )}

      {data?.education?.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionHeader}>Education</Text>
          {data.education.map((edu, index) => (
            <View key={index} style={styles.educationItem}>
              <View style={styles.educationHeader}>
                <Text style={styles.educationDegree}>{edu.degree || ''}</Text>
                <Text style={styles.educationDate}>
                  {edu.startDate && edu.endDate 
                    ? `${edu.startDate} - ${edu.endDate}`
                    : edu.endDate || ''
                  }
                </Text>
              </View>
              <Text style={styles.educationInstitution}>
                {edu.institutionName || ''}{edu.city ? `, ${edu.city}` : ''}
              </Text>
            </View>
          ))}
          {(data?.workExperience?.length > 0 || data?.internships?.length > 0 || data?.projects?.length > 0 || data?.certifications?.length > 0 || data?.skills?.length > 0 || data?.student?.extraCurriculars) && (
            <View style={styles.sectionDivider} />
          )}
        </View>
      )}

      {(data?.workExperience?.length > 0 || data?.internships?.length > 0) && (
        <View style={styles.section}>
          <Text style={styles.sectionHeader}>
            {data?.workExperience?.length > 0 && data?.internships?.length > 0
              ? 'Work Experience / Internship'
              : data?.workExperience?.length > 0
              ? 'Work Experience'
              : 'Internship'}
          </Text>
          {[...(data.workExperience || []), ...(data.internships || [])].map((exp, index) => (
            <View key={index} style={styles.experienceItem}>
              <View style={styles.experienceHeader}>
                <Text style={styles.experienceTitle}>{exp.roleName || ''}{exp.roleName && exp.companyName ? ', ' : ''}{exp.companyName || ''}</Text>
                <Text style={styles.experienceDate}>
                  {exp.startDate || ''}{exp.startDate ? ' - ' : ''}{exp.isCurrentlyWorking ? 'Present' : exp.endDate || ''}
                </Text>
              </View>
              <View style={styles.desPadding}>
              {convertToPlain(exp.description || '').split('\n').filter(line => line.trim()).map((line, index) => (
                <View key={index} style={styles.bulletLine}>
                  <Text style={styles.bullet}>•</Text>
                  <Text style={styles.bulletText}>{line.trim()}</Text>
                </View>
              ))}
              </View>
            </View>
          ))}
          {(data?.projects?.length > 0 || data?.certifications?.length > 0 || data?.skills?.length > 0 || data?.student?.extraCurriculars) && (
            <View style={styles.sectionDivider} />
          )}
        </View>
      )}

      {data?.projects?.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionHeader}>Projects</Text>
          {data?.projects?.map((project, index) => (
            <View key={index} style={styles.projectItem}>
              <View style={styles.projectHeader}>
                <Text style={styles.projectTitle}>{project.title || ''}</Text>
                <Text style={styles.projectDate}>
                  {project.startDate || ''}{project.startDate ? ' - ' : ''}{project.endDate || ''}
                </Text>
              </View>
              <View style={styles.desPadding}>
              {convertToPlain(project.description || '').split('\n').filter(line => line.trim()).map((line, index) => (
                <View key={index} style={styles.bulletLine}>
                  <Text style={styles.bullet}>•</Text>
                  <Text style={styles.bulletText}>{line.trim()}</Text>
                </View>
              ))}
              </View>
            </View>
          ))}
          {(data?.certifications?.length > 0 || data?.skills?.length > 0 || data?.student?.extraCurriculars) && (
            <View style={styles.sectionDivider} />
          )}
        </View>
      )}

      {data?.certifications?.length > 0 && (
        <View style={{ marginBottom: 4 }}>
          <Text style={styles.sectionHeader}>CERTIFICATIONS AND TRAINING</Text>
          {data?.certifications?.map((cert, index) => (
            <View key={index} style={styles.certification}>
              <Text style={styles.certificationBullet}>{'\u2022'}</Text>
              <Text style={styles.certificationText}>{cert?.courseName} in {cert?.instituteName}, {cert?.endYear}</Text>
            </View>
          ))}
          {(data?.skills?.length > 0 || data?.student?.extraCurriculars) && (
            <View style={styles.sectionDivider} />
          )}
        </View>
      )}

      {data?.skills?.length > 0 && (
        <View style={{ marginBottom: 4 }}>
          <Text style={styles.sectionHeader}>SKILLS</Text>
          <View style={styles.skills}>
            {data?.skills?.map((skill, index) => (
              <Text key={index}>
              {skill?.trim()}{index < data.skills.length - 1 ? ', ' : ''}
              </Text>
            ))}
          </View>
          {data?.student?.extraCurriculars && (
            <View style={styles.sectionDivider} />
          )}
        </View>
      )}

      {data?.student?.extraCurriculars && (
        <View style={styles.section}>
          <Text style={styles.sectionHeader1}>EXTRACURRICULAR ACTIVITIES</Text>
          <View style={styles.desPadding}>
          {convertToPlain(data?.student?.extraCurriculars).split('\n').filter(line => line.trim()).map((line, index) => (
            <View key={index} style={styles.bulletLine}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>{line.trim()}</Text>
            </View>
          ))}
          </View>
        </View>
      )}
    </Page>
  </Document>
);

export default NewResumeTemplate;
